import React, { useState, useEffect, useCallback } from 'react';
import { DollarSign, Key, Save, Eye, EyeOff, Loader2, RefreshCw, AlertCircle } from 'lucide-react';
import { Layout } from './Layout';
import { BudgetMonitor } from './BudgetMonitor';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { apiService } from '../services/api';
import { useAuth } from '../context/AuthContext';

export const BudgetPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [budgets, setBudgets] = useState([]);
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [apiKeys, setApiKeys] = useState({ ahrefs: '', youtube: '' });
  const [maskedKeys, setMaskedKeys] = useState({ ahrefs: '', youtube: '' });
  const [showKeys, setShowKeys] = useState({ ahrefs: false, youtube: false });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [dfsBudget, setDfsBudget] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const budgetRes = await apiService.getBudgetStatus();
      setBudgets(budgetRes.data.budgets || []);
      setActivity(budgetRes.data.activity || null);

      if (isAdmin) {
        const settingsRes = await apiService.getSettings();
        setMaskedKeys(settingsRes.data.api_keys || {});
        try {
          const dfsRes = await apiService.getDataForSEOBudget();
          setDfsBudget(dfsRes.data);
        } catch { setDfsBudget(null); }
      } else {
        // Client: DataForSEO comes inside budgets already
        const dfs = budgetRes.data.budgets?.find(b => b.service === 'dataforseo');
        if (dfs?.configured) setDfsBudget(dfs);
      }
    } catch (err) {
      console.error('Error loading budget data:', err);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSaveKey = async (service) => {
    if (!apiKeys[service]) return;
    setSaving(true);
    setMessage('');
    try {
      await apiService.updateSettings({ api_keys: { [service]: apiKeys[service] } });
      setMessage(`${service} API key updated successfully!`);
      setApiKeys(prev => ({ ...prev, [service]: '' }));
      loadData();
    } catch (err) {
      setMessage(`Error updating ${service} key: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // For clients: filter out youtube/google_suggest, only show ahrefs/seranking
  const openaiBudget = budgets.find(b => b.service === 'openai');
  const displayBudgets = isAdmin
    ? budgets
        .filter(b => b.service !== 'openai')
    : budgets.filter(b => b.service && !['youtube', 'google_suggest'].includes(b.service) && b.service !== 'dataforseo');

  if (loading) {
    return (
      <Layout activeTab="budget">
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-12 h-12 text-primary animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout activeTab="budget">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-display font-bold text-foreground">
            {isAdmin ? 'API Budget Monitor' : 'My API Budget'}
          </h2>
          <p className="text-sm text-muted-foreground">
            {isAdmin ? 'Track API usage and manage keys' : 'Track your connected API services'}
          </p>
        </div>
        <button onClick={loadData} className="flex items-center gap-2 px-3 py-2 text-sm bg-card border border-border rounded-md hover:bg-accent">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Client: no keys configured notice */}
      {!isAdmin && displayBudgets.length > 0 && displayBudgets.every(b => !b.configured && b.service !== 'dataforseo') && !dfsBudget?.configured && (
        <Card className="card-elevated mb-4 border-yellow-300">
          <CardContent className="pt-4 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-700 flex-shrink-0" />
            <div>
              <p className="text-sm text-foreground">No API keys configured yet.</p>
              <p className="text-xs text-muted-foreground">Go to Settings → Profile to add your DataForSEO, Ahrefs, or SE Ranking API keys.</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Budget cards (Ahrefs, SE Ranking for client; all for admin) */}
      {displayBudgets.length > 0 && (
        <div className="mb-8">
          <BudgetMonitor budgets={displayBudgets} />
        </div>
      )}

      {/* DataForSEO Budget */}
      {dfsBudget && (dfsBudget.configured !== false) && (
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-primary" />
            DataForSEO (SERP)
          </h3>
          <Card className="card-elevated">
            <CardContent className="pt-4">
              {dfsBudget.error ? (
                <p className="text-sm text-red-700">Error: {dfsBudget.error}</p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Balance</p>
                    <p className={`text-2xl font-bold ${(dfsBudget.balance || 0) > 1 ? 'text-emerald-700' : 'text-red-700'}`}>
                      ${dfsBudget.balance?.toFixed(2) || '—'}
                    </p>
                  </div>
                  {isAdmin && (
                    <>
                      <div>
                        <p className="text-sm text-muted-foreground">Total Spent</p>
                        <p className="text-2xl font-bold text-foreground">${dfsBudget.total_cost?.toFixed(2) || '0'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Total Requests</p>
                        <p className="text-2xl font-bold text-foreground">{dfsBudget.total_requests?.toLocaleString() || 0}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Today</p>
                        <p className="text-2xl font-bold text-foreground">{dfsBudget.today_requests || 0}</p>
                      </div>
                    </>
                  )}
                  {!isAdmin && (
                    <div>
                      <p className="text-sm text-muted-foreground">Currency</p>
                      <p className="text-2xl font-bold text-foreground">{dfsBudget.currency || 'USD'}</p>
                    </div>
                  )}
                </div>
              )}
              {isAdmin && dfsBudget.balance != null && (
                <div className="mt-3 pt-3 border-t border-border flex items-center justify-between">
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <span>~${dfsBudget.cost_per_request || 0.005}/req</span>
                    <span>Est. remaining: ~{Math.floor((dfsBudget.balance || 0) / (dfsBudget.cost_per_request || 0.005)).toLocaleString()} requests</span>
                  </div>
                  <div className="w-32 h-1.5 bg-background rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${(dfsBudget.total_cost || 0) > 4 ? 'bg-red-500' : (dfsBudget.total_cost || 0) > 2 ? 'bg-yellow-500' : 'bg-emerald-500'}`}
                      style={{ width: `${Math.min(((dfsBudget.total_cost || 0) / 5) * 100, 100)}%` }}
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* OpenAI fallback usage (admin) */}
      {isAdmin && openaiBudget && (
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-primary" />
            OpenAI Brand Fallback
          </h3>
          <Card className="card-elevated">
            <CardContent className="pt-4">
              {!openaiBudget.configured ? (
                <p className="text-sm text-muted-foreground">OpenAI API key is not configured.</p>
              ) : (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Mode</p>
                      <p className={`text-2xl font-bold ${openaiBudget.enabled ? 'text-emerald-700' : 'text-yellow-700'}`}>
                        {openaiBudget.enabled ? 'LIVE' : 'DISABLED'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Today Tokens</p>
                      <p className="text-2xl font-bold text-foreground">{(openaiBudget.today_total_tokens || 0).toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Today Cost (est)</p>
                      <p className="text-2xl font-bold text-foreground">${(openaiBudget.today_estimated_cost_usd || 0).toFixed(4)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Today Runs</p>
                      <p className="text-2xl font-bold text-foreground">{openaiBudget.today_runs || 0}</p>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-border grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground">Total Tokens</p>
                      <p className="text-sm font-semibold text-foreground">{(openaiBudget.total_tokens || 0).toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Total Cost (est)</p>
                      <p className="text-sm font-semibold text-foreground">${(openaiBudget.total_estimated_cost_usd || 0).toFixed(4)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Model</p>
                      <p className="text-sm font-semibold text-foreground">{openaiBudget.model || 'gpt-4o-mini'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Total Runs</p>
                      <p className="text-sm font-semibold text-foreground">{openaiBudget.total_runs || 0}</p>
                    </div>
                  </div>
                  {openaiBudget.note && (
                    <p className="text-xs text-muted-foreground mt-3">{openaiBudget.note}</p>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Activity stats - Admin only */}
      {isAdmin && activity && (
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-foreground mb-3">Collection Activity</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="card-elevated">
              <CardContent className="pt-4">
                <p className="text-sm text-muted-foreground">Total Signals</p>
                <p className="text-2xl font-bold text-foreground">{activity.total_signals?.toLocaleString()}</p>
              </CardContent>
            </Card>
            <Card className="card-elevated">
              <CardContent className="pt-4">
                <p className="text-sm text-muted-foreground">Today's Signals</p>
                <p className="text-2xl font-bold text-foreground">{activity.today_signals?.toLocaleString()}</p>
              </CardContent>
            </Card>
            <Card className="card-elevated">
              <CardContent className="pt-4">
                <p className="text-sm text-muted-foreground">Entities Found</p>
                <p className="text-2xl font-bold text-foreground">{activity.total_entities}</p>
              </CardContent>
            </Card>
            <Card className="card-elevated">
              <CardContent className="pt-4">
                <p className="text-sm text-muted-foreground">Total Spikes</p>
                <p className="text-2xl font-bold text-foreground">{activity.total_spikes}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* API Keys Management - Admin only */}
      {isAdmin && (
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
            <Key className="w-5 h-5 text-primary" />
            API Keys
          </h3>

          {message && (
            <div className={`mb-4 p-3 rounded-md text-sm ${message.includes('Error') ? 'bg-destructive/10 text-destructive' : 'bg-green-50 text-green-700'}`}>
              {message}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Ahrefs */}
            <Card className="card-elevated">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold">Ahrefs API Key</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center gap-2 p-2 bg-background rounded border border-border">
                    <span className="text-sm text-muted-foreground font-mono">
                      Current: {maskedKeys.ahrefs || 'NOT SET'}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <input
                        type={showKeys.ahrefs ? 'text' : 'password'}
                        placeholder="Enter new Ahrefs API key..."
                        value={apiKeys.ahrefs}
                        onChange={(e) => setApiKeys(prev => ({ ...prev, ahrefs: e.target.value }))}
                        className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-foreground"
                      />
                      <button
                        onClick={() => setShowKeys(prev => ({ ...prev, ahrefs: !prev.ahrefs }))}
                        className="absolute right-2 top-2 text-muted-foreground hover:text-foreground"
                      >
                        {showKeys.ahrefs ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    <button
                      onClick={() => handleSaveKey('ahrefs')}
                      disabled={!apiKeys.ahrefs || saving}
                      className="px-3 py-2 bg-primary text-primary-foreground rounded-md text-sm hover:bg-primary/90 disabled:opacity-50"
                    >
                      <Save className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* YouTube */}
            <Card className="card-elevated">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold">Google / YouTube API Key</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center gap-2 p-2 bg-background rounded border border-border">
                    <span className="text-sm text-muted-foreground font-mono">
                      Current: {maskedKeys.youtube || 'NOT SET'}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <input
                        type={showKeys.youtube ? 'text' : 'password'}
                        placeholder="Enter new YouTube API key..."
                        value={apiKeys.youtube}
                        onChange={(e) => setApiKeys(prev => ({ ...prev, youtube: e.target.value }))}
                        className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-foreground"
                      />
                      <button
                        onClick={() => setShowKeys(prev => ({ ...prev, youtube: !prev.youtube }))}
                        className="absolute right-2 top-2 text-muted-foreground hover:text-foreground"
                      >
                        {showKeys.youtube ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    <button
                      onClick={() => handleSaveKey('youtube')}
                      disabled={!apiKeys.youtube || saving}
                      className="px-3 py-2 bg-primary text-primary-foreground rounded-md text-sm hover:bg-primary/90 disabled:opacity-50"
                    >
                      <Save className="w-4 h-4" />
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground">Free tier: 10,000 units/day</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </Layout>
  );
};
